import React, { useState, useEffect } from 'react';
import SiteNav from '@/components/SiteNav';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Globe, Eye, EyeOff, CheckCircle, XCircle, Loader2 } from 'lucide-react';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [verifying, setVerifying] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setVerifying(false); return; }
    api.verifyResetToken(token)
      .then(res => {
        setValid(res.data.valid);
        setEmail(res.data.email || '');
      })
      .catch(() => setValid(false))
      .finally(() => setVerifying(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await api.resetPassword(token, password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center" data-testid="reset-verifying">
        <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-6 pt-24 pb-10" data-testid="reset-password-page">
      <SiteNav />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6 min-h-[44px]">
            <div className="w-10 h-10 bg-indigo-500 rounded-xl flex items-center justify-center">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>EasyPeazy</span>
          </Link>
        </div>

        {!token || !valid ? (
          <div className="glass-card p-8 text-center">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Outfit' }}>This link has expired</h2>
            <p className="text-slate-400 mb-6">Reset links work once and expire after a short time. Ask for a fresh one and we'll email it right away.</p>
            <Link to="/forgot-password" className="inline-flex items-center justify-center min-h-[44px] px-5 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white font-semibold" data-testid="request-new-link">Send me a new link</Link>
            <div className="mt-3"><Link to="/login" className="inline-flex items-center min-h-[44px] px-3 text-indigo-300 hover:text-white">Back to sign in</Link></div>
          </div>
        ) : success ? (
          <div className="glass-card p-8 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Outfit' }}>Password Reset!</h2>
            <p className="text-slate-400 mb-4">Your password has been changed. Redirecting to login...</p>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-bold text-white mb-2 text-center" style={{ fontFamily: 'Outfit' }}>New Password</h1>
            <p className="text-slate-400 text-center mb-6">Set a new password for {email}</p>

            <form onSubmit={handleSubmit} className="glass-card p-8 space-y-5">
              {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm" data-testid="reset-error">{error}</div>}

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">New Password</label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-slate-900/50 border border-slate-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg h-12 px-4 pr-12 text-slate-100 placeholder:text-slate-600 transition-all outline-none"
                    placeholder="Min 6 characters"
                    required
                    data-testid="reset-password-input"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                    {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Confirm Password</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full bg-slate-900/50 border border-slate-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg h-12 px-4 text-slate-100 placeholder:text-slate-600 transition-all outline-none"
                  placeholder="Confirm password"
                  required
                  data-testid="reset-confirm-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-indigo-500 hover:bg-indigo-600 text-white h-12 rounded-lg font-medium transition-all active:scale-95 disabled:opacity-50 shadow-lg shadow-indigo-500/20"
                data-testid="reset-submit-btn"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
